import { useEffect } from "react";
import { Link } from "react-router-dom";
import { successStories, blogPosts, tools } from "../../data/mock";

/**
 * Human-readable sitemap page.
 *
 * This complements, not replaces, /sitemap.xml (the machine-readable file
 * referenced in robots.txt for search engines). This page is for visitors
 * who click "Sitemap" in the footer and want a quick overview of everything
 * on the site.
 *
 * Note: imports `successStories`, `blogPosts`, and `tools` directly from
 * mock.js so this list never drifts out of sync with the real content. If
 * your export names in mock.js differ from these three, adjust the import
 * at the top of this file accordingly.
 */

export default function Sitemap() {
  useEffect(() => {
    document.title = "Sitemap | AbhinavJoshi";
  }, []);

  return (
    <main className="bg-white text-[#0f1720]">
      <div className="mx-auto max-w-4xl px-6 py-16 md:py-24">
        <header className="max-w-2xl">
          <h1 className="text-4xl font-semibold leading-tight text-[#000d14] md:text-5xl">
            Sitemap
          </h1>
          <p className="mt-5 text-base leading-relaxed text-[#0f1720]/70 md:text-lg">
            A quick overview of every page on abhinavjoshi.com. Looking for
            the machine-readable version for search engines instead? See{" "}
            <a
              href="/sitemap.xml"
              className="underline underline-offset-2 hover:text-[#000d14]"
            >
              sitemap.xml
            </a>
            .
          </p>
        </header>

        <div className="mt-14 space-y-12">
          <SitemapSection title="Main pages">
            <SitemapLink to="/">Home</SitemapLink>
            <SitemapLink to="/about">About</SitemapLink>
            <SitemapLink to="/connect">Contact</SitemapLink>
          </SitemapSection>

          <SitemapSection title="Success stories">
            <SitemapLink to="/success-stories">All success stories</SitemapLink>
            {successStories.map((story) => (
              <SitemapLink key={story.slug} to={`/success-stories/${story.slug}`}>
                {story.listingTitle || story.clientName}
              </SitemapLink>
            ))}
          </SitemapSection>

          <SitemapSection title="Blog">
            <SitemapLink to="/category/blogs">All blog posts</SitemapLink>
            {blogPosts.map((post) => (
              <SitemapLink key={post.slug} to={`/blog/${post.slug}`}>
                {post.title}
              </SitemapLink>
            ))}
          </SitemapSection>

          <SitemapSection title="Tools">
            {tools.map((tool) => (
              <a
                key={tool.href}
                href={tool.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-[15px] text-[#0f1720]/80 underline-offset-2 hover:text-[#000d14] hover:underline"
              >
                {tool.title}
              </a>
            ))}
          </SitemapSection>

          <SitemapSection title="Legal">
            <SitemapLink to="/privacy-policy">Privacy Policy</SitemapLink>
          </SitemapSection>
        </div>
      </div>
    </main>
  );
}

function SitemapSection({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-[#000d14]">{title}</h2>
      <ul className="mt-4 space-y-2">
        {Array.isArray(children)
          ? children.map((child, i) => <li key={i}>{child}</li>)
          : <li>{children}</li>}
      </ul>
    </section>
  );
}

function SitemapLink({ to, children }) {
  return (
    <Link
      to={to}
      className="block text-[15px] text-[#0f1720]/80 underline-offset-2 hover:text-[#000d14] hover:underline"
    >
      {children}
    </Link>
  );
}
